import DiagonalCurrencyPair from 'components/DiagonalCurrencyPairLogo'
import { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { AnalyticsVersion } from 'types/analytics'
import { cn } from 'utils/analytics/cn'
import { formatPercentChange } from 'utils/analytics/formatPercentChange'
import { formatPriceWithMantissa } from 'utils/formatCurrencyAmount'
import { isMobile } from 'utils/userAgent'

import CurrencyLogo from '../../../CurrencyLogo'
import { SkeletonBar, SkeletonCircle } from '../../Skeletons'
import DoubleBadge from './DoubleBadge'
import ExternalLink from './ExternalLink'

type AssetHeaderProps = {
  logoURI?: string | string[]
  name?: string
  symbol?: string
  version?: AnalyticsVersion
  feeTier?: string
  links?: string[]
  nameLinks?: {
    label: string
    href: string
  }[]
  price?: {
    value?: number
    change24h?: number
  }
}

export default function AssetHeader({
  logoURI,
  name,
  symbol,
  version,
  feeTier,
  links,
  price,
  nameLinks,
}: AssetHeaderProps) {
  const renderDoubleLogo = (logos: string[]) => {
    const sanitized = logos.filter((uri): uri is string => Boolean(uri))
    if (sanitized.length === 0) return null

    if (isMobile) {
      return (
        <div>
          <DiagonalCurrencyPair logo0={sanitized[0]} logo1={sanitized[1]} />
        </div>
      )
    }

    return (
      <div className="flex -space-x-2">
        {sanitized.slice(0, 2).map((uri, index) => (
          <CurrencyLogo
            key={`${uri}-${index}`}
            logoURI={uri}
            size="32px"
            className="rounded-full border-2 border-gray-800 bg-gray-900 object-cover"
            needFallbackOnLoading
          />
        ))}
      </div>
    )
  }

  return (
    <div className="flex justify-between gap-3">
      <div className="flex gap-2 items-center">
        {logoURI ? (
          <>
            {typeof logoURI === 'string' && (
              <CurrencyLogo
                logoURI={logoURI}
                symbol={symbol}
                size="28px"
                className="rounded-full border-2 border-gray-800 bg-gray-900 object-cover"
                needFallbackOnLoading
              />
            )}

            {Array.isArray(logoURI) &&
              (logoURI.length === 1 ? (
                <CurrencyLogo
                  logoURI={logoURI[0]}
                  symbol={symbol}
                  size="28px"
                  className="rounded-full border-2 border-gray-800 bg-gray-900 object-cover"
                  needFallbackOnLoading
                />
              ) : (
                renderDoubleLogo(logoURI)
              ))}
          </>
        ) : (
          <SkeletonCircle size="7" />
        )}

        <div className="flex flex-col sm:flex-row sm:gap-3">
          {nameLinks?.length ? (
            <div className="flex items-center gap-1">
              {nameLinks.map((token, index) => (
                <Fragment key={`${token.href}-${token.label}`}>
                  {index > 0 && <span className="text-kodiak-text-secondary px-1">/</span>}
                  <Link
                    to={token.href}
                    className={cn(
                      'text-md md:text-2xl font-medium text-kodiak-text-primary transition-colors hover:text-kodiak-text-secondary rounded-sm',
                      'max-w-[110px] md:max-w-max overflow-hidden text-ellipsis whitespace-nowrap'
                    )}
                  >
                    {token.label}
                  </Link>
                </Fragment>
              ))}
            </div>
          ) : (
            <span className="text-md md:text-2xl font-medium text-kodiak-text-primary max-w-[110px] md:max-w-max overflow-hidden text-ellipsis whitespace-nowrap">
              {name}
            </span>
          )}

          {symbol && <span className="text-md md:text-2xl font-medium text-kodiak-text-secondary">{symbol}</span>}
        </div>

        {version && feeTier && <DoubleBadge version={version} feeTier={feeTier} />}
      </div>

      <div className="flex gap-2 xl:gap-5 items-center justify-between sm:justify-start sm:w-auto">
        <div className="flex gap-2 items-center">
          {price && (
            <div className="flex gap-2 items-center">
              {price.value ? (
                <span className="text-md sm:text-2xl font-bold">{formatPriceWithMantissa(price.value)}</span>
              ) : (
                <SkeletonBar className="w-16 h-5" />
              )}

              {price.change24h ? (
                <span
                  className={cn(
                    'text-sm sm:text-xl font-medium min-w-20',
                    `${formatPercentChange(price.change24h)?.className}`
                  )}
                >
                  {formatPercentChange(price.change24h)?.text}
                </span>
              ) : price.change24h === 0 ? (
                <></>
              ) : (
                <div>
                  <SkeletonBar className="w-20 h-4" opacity="bg-gray-800/50" />
                </div>
              )}
            </div>
          )}
        </div>
        {links && (
          <div className="flex gap-2 items-center ml-auto sm:ml-0">
            {links.map((link) => (
              <ExternalLink key={link} link={link} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
